import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, FileDown, Sparkles, Bell, Loader2, ShieldAlert, AlertOctagon, Crown, Heart } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, isToday, isFuture, getDay, parseISO } from "date-fns";
import Header from "@/components/Header";
import DayModal from "@/components/DayModal";
import FutureDayModal from "@/components/FutureDayModal";
import CellulitisTracker from "@/components/CellulitisTracker";
import EmergencyScript from "@/components/EmergencyScript";
import WelcomeGreeting from "@/components/WelcomeGreeting";
import WeeklyRecap from "@/components/WeeklyRecap";
import ReminderSettings, { useReminders } from "@/components/ReminderSettings";
import LymphieChat from "@/components/LymphieChat";
import TourOverlay from "@/components/TourOverlay";
import FriendSummary from "@/components/FriendSummary";
import ThemePicker from "@/components/ThemePicker";
import SignupDisclaimer from "@/components/SignupDisclaimer";
import UpgradeModal from "@/components/UpgradeModal";
import DonateModal from "@/components/DonateModal";
import DonationProgress from "@/components/DonationProgress";
import TrialBanner from "@/components/TrialBanner";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

const MOOD_EMOJIS = {
  happy: "😊", calm: "😌", hopeful: "🥺", sad: "😔",
  anxious: "😣", tired: "😴", frustrated: "😤", grateful: "🥰",
};

function CalendarCell({ day, entry, eventCount, hasCellulitis, onClick, isFutureDate, isCurrentDay }) {
  const dateKey = format(day, "yyyy-MM-dd");
  const hasEntry = !!entry;
  const flare = entry?.flare_up;
  const emoji = entry?.mood_emoji ? MOOD_EMOJIS[entry.mood_emoji] || entry.mood_emoji : null;
  const sc = entry?.self_care || {};
  const scDots = ["compression", "massage", "elevation", "bandaging"].filter((k) => sc[k]);

  return (
    <motion.button
      layoutId={`day-${dateKey}`}
      onClick={() => onClick(dateKey, isFutureDate)}
      data-testid={`day-cell-${dateKey}`}
      data-cellulitis={hasCellulitis ? "true" : "false"}
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.97 }}
      className={`
        relative aspect-square rounded-2xl p-2 md:p-3 flex flex-col items-start justify-between text-left
        border transition-shadow
        ${hasCellulitis ? 'bg-gradient-to-br from-[#FFE5E5] to-[#FFB7B2]/50 border-[#E63946] shadow-md hover:shadow-lg ring-1 ring-[#E63946]/30'
          : isFutureDate
          ? (eventCount > 0
              ? 'bg-white/80 border-kawaii-lavender/60 shadow-sm hover:shadow-md'
              : 'bg-white/40 border-kawaii-border hover:bg-white/70 hover:shadow-sm')
          : flare ? 'bg-kawaii-peach/60 border-kawaii-peach shadow-sm hover:shadow-md'
          : hasEntry ? 'bg-white border-kawaii-pink/40 shadow-sm hover:shadow-md'
          : 'bg-white/70 border-kawaii-border hover:shadow-md'}
        ${isCurrentDay ? 'ring-2 ring-kawaii-pink ring-offset-2 ring-offset-transparent' : ''}
      `}
    >
      {hasCellulitis && (
        <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-bold text-white bg-[#E63946] rounded-full px-1.5 py-0.5">
          <ShieldAlert className="w-2.5 h-2.5" strokeWidth={3} />
        </span>
      )}
      <div className="flex items-center justify-between w-full">
        <span className={`font-heading text-sm md:text-base font-semibold ${hasCellulitis ? 'text-[#E63946]' : isCurrentDay ? 'text-kawaii-pink' : isFutureDate ? 'text-kawaii-textSoft' : 'text-kawaii-text'}`}>
          {format(day, "d")}
        </span>
        {emoji && !hasCellulitis && <span className="text-lg md:text-xl leading-none">{emoji}</span>}
        {isFutureDate && eventCount > 0 && !hasCellulitis && (
          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-gradient-to-br from-kawaii-lavender to-kawaii-pink text-white text-[10px] font-bold" data-testid={`event-count-${dateKey}`}>
            {eventCount}
          </span>
        )}
      </div>
      <div className="w-full flex items-end justify-between">
        <div className="flex gap-0.5">
          {scDots.slice(0, 4).map((k) => (
            <span key={k} className="w-1.5 h-1.5 rounded-full bg-kawaii-mint" />
          ))}
        </div>
        {flare && <span className="text-[9px] uppercase tracking-wider text-kawaii-textSoft font-bold">flare</span>}
      </div>
    </motion.button>
  );
}

export default function CalendarPage() {
  useReminders();
  const { user } = useAuth();
  const [current, setCurrent] = useState(new Date());
  const [entries, setEntries] = useState({});
  const [eventCounts, setEventCounts] = useState({});
  const [cellulitisDates, setCellulitisDates] = useState(new Set());
  const [showCellulitis, setShowCellulitis] = useState(false);
  const [pastCellulitisDate, setPastCellulitisDate] = useState(null);
  const [showEmergency, setShowEmergency] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showDonate, setShowDonate] = useState(false);
  const [emergencySeed, setEmergencySeed] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedIsFuture, setSelectedIsFuture] = useState(false);
  const [showRecap, setShowRecap] = useState(false);
  const [showReminders, setShowReminders] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [stats, setStats] = useState({ care_points: 0, streak_days: 0, total_entries: 0 });

  const monthKey = format(current, "yyyy-MM");
  const days = useMemo(() => eachDayOfInterval({ start: startOfMonth(current), end: endOfMonth(current) }), [current]);
  const startPad = getDay(startOfMonth(current));

  const loadMonth = async () => {
    try {
      const { data } = await api.get(`/entries?month=${monthKey}`);
      const map = {};
      data.forEach((e) => { map[e.date] = e; });
      setEntries(map);
    } catch (e) {
      console.error(e);
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get("/stats");
      setStats(data);
    } catch (e) { console.error(e); }
  };

  const loadEvents = async () => {
    try {
      const { data } = await api.get(`/events?month=${monthKey}`);
      const counts = {};
      data.forEach((ev) => { counts[ev.date] = (counts[ev.date] || 0) + 1; });
      setEventCounts(counts);
    } catch (e) { console.error(e); }
  };

  const loadCellulitis = async () => {
    try {
      const { data } = await api.get(`/cellulitis/active-dates?month=${monthKey}`);
      setCellulitisDates(new Set(data.dates || []));
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadMonth(); loadEvents(); loadCellulitis(); /* eslint-disable-next-line */ }, [monthKey]);
  useEffect(() => { loadStats(); }, []);

  useEffect(() => {
    const handler = (e) => {
      setEmergencySeed(e.detail?.answers || null);
      setShowCellulitis(false);
      setShowEmergency(true);
    };
    window.addEventListener("open-emergency", handler);
    const pastHandler = (e) => {
      setPastCellulitisDate(e.detail?.date || null);
      setShowCellulitis(true);
    };
    window.addEventListener("open-cellulitis-past", pastHandler);
    return () => {
      window.removeEventListener("open-emergency", handler);
      window.removeEventListener("open-cellulitis-past", pastHandler);
    };
  }, []);

  const openDay = (dateKey, isFutureFlag) => {
    setSelectedIsFuture(!!isFutureFlag);
    setSelectedDate(dateKey);
  };
  const closeDay = () => setSelectedDate(null);
  const onSaved = (entry) => {
    setEntries((prev) => ({ ...prev, [entry.date]: entry }));
    loadStats();
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("lc_token");
      const res = await fetch(`${API}/export/pdf?month=${monthKey}&auth=${token}`);
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lymphedema-log-${monthKey}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF saved to downloads ♡");
    } catch (e) {
      toast.error("Could not generate PDF");
    } finally {
      setExporting(false);
    }
  };

  const monthEntriesCount = Object.keys(entries).length;
  const flareCount = Object.values(entries).filter((e) => e.flare_up).length;

  const todayKey = format(new Date(), "yyyy-MM-dd");
  const todayEntry = entries[todayKey];

  const recentReflections = useMemo(() => {
    return Object.values(entries)
      .filter((e) => e.summary)
      .sort((a, b) => (a.date < b.date ? 1 : -1))
      .slice(0, 3);
  }, [entries]);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <FriendSummary />

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-kawaii-textSoft font-semibold">Lymphedema daily log</p>
            <h1 className="font-heading text-4xl md:text-5xl font-semibold text-kawaii-text mt-1">
              {format(current, "MMMM yyyy")}
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setCurrent(subMonths(current, 1))}
              data-testid="prev-month"
              className="kawaii-btn w-11 h-11 rounded-full bg-white border border-kawaii-border flex items-center justify-center"
            >
              <ChevronLeft className="w-5 h-5 text-kawaii-text" strokeWidth={2.5} />
            </button>
            <button
              onClick={() => setCurrent(new Date())}
              data-testid="today-btn"
              className="kawaii-btn px-5 h-11 rounded-full bg-white border border-kawaii-border font-body font-semibold text-sm text-kawaii-text"
            >
              Today
            </button>
            <button
              onClick={() => setCurrent(addMonths(current, 1))}
              data-testid="next-month"
              className="kawaii-btn w-11 h-11 rounded-full bg-white border border-kawaii-border flex items-center justify-center"
            >
              <ChevronRight className="w-5 h-5 text-kawaii-text" strokeWidth={2.5} />
            </button>
          </div>
        </div>

        <TrialBanner onUpgrade={() => setShowUpgrade(true)} />

        <div className="flex flex-wrap items-center gap-2 mb-6">
          <button
            onClick={() => setShowEmergency(true)}
            data-testid="open-emergency"
            title="Urgent care script"
            className="kawaii-btn px-4 py-2 rounded-full bg-gradient-to-r from-[#FF6B6B] to-[#E63946] text-white font-body font-semibold text-sm shadow-md inline-flex items-center gap-2"
          >
            <AlertOctagon className="w-4 h-4" strokeWidth={2.5} /> Emergency
          </button>
          <button
            onClick={() => setShowCellulitis(true)}
            data-testid="open-cellulitis"
            className="kawaii-btn px-4 py-2 rounded-full bg-white border border-[#E63946]/40 font-body font-semibold text-sm text-[#E63946] inline-flex items-center gap-2"
          >
            <ShieldAlert className="w-4 h-4" strokeWidth={2.5} /> Cellulitis
          </button>
          <button
            onClick={() => setShowRecap(true)}
            data-testid="open-weekly-recap"
            className="kawaii-btn px-4 py-2 rounded-full bg-gradient-to-r from-kawaii-pink to-kawaii-peach text-white font-body font-semibold text-sm shadow-sm inline-flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" strokeWidth={2.5} /> Weekly recap
          </button>
          <button
            onClick={exportPdf}
            disabled={exporting}
            data-testid="export-pdf-btn"
            className="kawaii-btn px-4 py-2 rounded-full bg-white border border-kawaii-border font-body font-semibold text-sm text-kawaii-text inline-flex items-center gap-2 disabled:opacity-60"
          >
            <FileDown className="w-4 h-4" strokeWidth={2.5} /> {exporting ? "Preparing…" : "Export PDF for doctor"}
          </button>
          <button
            onClick={() => setShowReminders(true)}
            data-testid="open-reminders"
            className="kawaii-btn px-4 py-2 rounded-full bg-white border border-kawaii-border font-body font-semibold text-sm text-kawaii-text inline-flex items-center gap-2"
          >
            <Bell className="w-4 h-4" strokeWidth={2.5} /> Reminders
          </button>
          {!user?.pro?.is_pro && (
            <button
              onClick={() => setShowUpgrade(true)}
              data-testid="open-upgrade"
              className="kawaii-btn px-4 py-2 rounded-full bg-gradient-to-r from-kawaii-lavender to-kawaii-pink text-white font-body font-semibold text-sm shadow-sm inline-flex items-center gap-2"
            >
              <Crown className="w-4 h-4" strokeWidth={2.5} /> Upgrade to Pro
            </button>
          )}
          {user?.pro?.source === "trial" && (
            <button
              onClick={() => setShowUpgrade(true)}
              data-testid="open-upgrade"
              className="kawaii-btn px-4 py-2 rounded-full bg-white border border-kawaii-lavender/60 text-kawaii-text font-body font-semibold text-sm inline-flex items-center gap-2"
            >
              <Crown className="w-4 h-4 text-kawaii-lavender" strokeWidth={2.5} />
              Keep Pro ({user.pro.trial_days_left}d left)
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="kawaii-card p-5 relative overflow-hidden" data-testid="care-points-card">
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-kawaii-pink/20 blur-2xl" />
            <div className="relative flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-kawaii-pink to-kawaii-peach flex items-center justify-center text-white text-2xl">⭐</div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-kawaii-textSoft font-bold">Care points</div>
                <div className="font-heading text-2xl font-semibold text-kawaii-text" data-testid="stat-care-points">
                  {stats.care_points.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
          <div className="kawaii-card p-5 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-kawaii-lavender/40 flex items-center justify-center text-2xl">🔥</div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-kawaii-textSoft font-bold">Streak</div>
              <div className="font-heading text-2xl font-semibold text-kawaii-text" data-testid="stat-streak">
                {stats.streak_days} <span className="text-sm text-kawaii-textSoft font-body">days</span>
              </div>
            </div>
          </div>
          <div className="kawaii-card p-5 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-kawaii-mint/60 flex items-center justify-center text-2xl">🌸</div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-kawaii-textSoft font-bold">Entries this month</div>
              <div className="font-heading text-2xl font-semibold text-kawaii-text" data-testid="stat-entries">{monthEntriesCount}</div>
            </div>
          </div>
          <div className="kawaii-card p-5 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-kawaii-peach/40 flex items-center justify-center text-2xl">🌡️</div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-kawaii-textSoft font-bold">Flare-ups</div>
              <div className="font-heading text-2xl font-semibold text-kawaii-text" data-testid="stat-flares">{flareCount}</div>
            </div>
          </div>
        </div>

        <div className="kawaii-card p-4 md:p-6">
          <div className="grid grid-cols-7 gap-2 md:gap-4 mb-2 md:mb-4">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="text-center text-xs md:text-sm uppercase tracking-widest text-kawaii-textSoft font-semibold">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-2 md:gap-4">
            {Array.from({ length: startPad }).map((_, i) => <div key={`pad-${i}`} />)}
            {days.map((day) => {
              const key = format(day, "yyyy-MM-dd");
              return (
                <CalendarCell
                  key={key}
                  day={day}
                  entry={entries[key]}
                  eventCount={eventCounts[key] || 0}
                  hasCellulitis={cellulitisDates.has(key)}
                  onClick={openDay}
                  isFutureDate={isFuture(day) && !isToday(day)}
                  isCurrentDay={isToday(day)}
                />
              );
            })}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-kawaii-textSoft font-body">
          <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#E63946]" /> Cellulitis</div>
          <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-kawaii-peach" /> Flare-up day</div>
          <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-kawaii-mint" /> Self-care logged</div>
          <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-kawaii-lavender" /> Upcoming events</div>
          <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full border-2 border-kawaii-pink" /> Today</div>
        </div>

        {/* Today's reflection */}
        {todayEntry?.summary && (
          <div className="mt-8 kawaii-card p-6 md:p-8 relative overflow-hidden" data-testid="today-reflection-card">
            <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-kawaii-pink/25 blur-2xl" />
            <div className="relative flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-kawaii-lavender to-kawaii-pink flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-kawaii-textSoft font-bold">Today's reflection</p>
                  <span className="text-kawaii-textSoft/60 text-xs">·</span>
                  <p className="text-xs text-kawaii-textSoft">{format(new Date(), "EEE, MMM d")}</p>
                </div>
                <p className="mt-2 font-body text-base text-kawaii-text leading-relaxed">{todayEntry.summary}</p>
              </div>
              <button
                onClick={() => openDay(todayKey)}
                data-testid="edit-today-btn"
                className="kawaii-btn px-4 py-2 rounded-full bg-white border border-kawaii-border text-sm font-body font-semibold text-kawaii-text hidden md:block"
              >
                Open
              </button>
            </div>
          </div>
        )}

        {/* Recent reflections */}
        {recentReflections.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs uppercase tracking-[0.25em] text-kawaii-textSoft font-semibold">Recent reflections</p>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {recentReflections.map((e) => (
                <button
                  key={e.date}
                  onClick={() => openDay(e.date)}
                  data-testid={`reflection-${e.date}`}
                  className="kawaii-btn text-left kawaii-card p-5 hover:shadow-md relative overflow-hidden"
                >
                  <div className={`absolute top-0 left-0 h-1 w-full ${e.flare_up ? 'bg-kawaii-peach' : 'bg-kawaii-lavender'}`} />
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-heading font-semibold text-kawaii-text">
                      {format(parseISO(e.date), "EEE, MMM d")}
                    </p>
                    {e.mood_emoji && (
                      <span className="text-2xl leading-none">
                        {({happy:"😊",calm:"😌",grateful:"🥰",hopeful:"🥺",tired:"😴",sad:"😔",anxious:"😣",frustrated:"😤"})[e.mood_emoji] || ""}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-kawaii-text/85 font-body leading-relaxed line-clamp-4">{e.summary}</p>
                </button>
              ))}
            </div>
          </div>
        )}
      </main>

      <WelcomeGreeting />

      <AnimatePresence>
        {selectedDate && !selectedIsFuture && (
          <DayModal
            dateKey={selectedDate}
            initialEntry={entries[selectedDate]}
            onClose={closeDay}
            onSaved={onSaved}
          />
        )}
        {selectedDate && selectedIsFuture && (
          <FutureDayModal
            dateKey={selectedDate}
            onClose={closeDay}
            onChanged={loadEvents}
          />
        )}
        {showRecap && <WeeklyRecap onClose={() => setShowRecap(false)} />}
        {showReminders && <ReminderSettings onClose={() => setShowReminders(false)} />}
        {showCellulitis && <CellulitisTracker onClose={() => { setShowCellulitis(false); setPastCellulitisDate(null); }} onChanged={loadCellulitis} initialPastDate={pastCellulitisDate} />}
        {showEmergency && <EmergencyScript initialAnswers={emergencySeed} onClose={() => { setShowEmergency(false); setEmergencySeed(null); }} />}
        {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
        {showDonate && <DonateModal onClose={() => setShowDonate(false)} />}
      </AnimatePresence>

      <TourOverlay />
      <LymphieChat />
      <ThemePicker />
      <SignupDisclaimer />

      <footer className="mt-16 pb-8 text-center text-[11px] text-kawaii-textSoft font-body">
        <DonationProgress onDonate={() => setShowDonate(true)} />
        <p className="max-w-md mx-auto italic mb-2 mt-4">
          MyLymphieLife is a tracking tool, not medical advice. In an emergency, call your local emergency service first.
        </p>
        <div className="flex items-center justify-center flex-wrap gap-x-4 gap-y-2">
          <button
            onClick={() => setShowDonate(true)}
            data-testid="footer-donate"
            className="kawaii-btn inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/70 border border-kawaii-pink/30 text-kawaii-text hover:bg-kawaii-pink/15"
          >
            <Heart className="w-3 h-3 text-kawaii-pink" strokeWidth={2.5} fill="#F4A8CD" />
            <span className="font-semibold">Support the app</span>
          </button>
          <Link to="/about" data-testid="footer-about" className="hover:text-kawaii-text underline underline-offset-2">About Jen</Link>
          <span>·</span>
          <Link to="/terms" data-testid="footer-terms" className="hover:text-kawaii-text underline underline-offset-2">Terms</Link>
          <span>·</span>
          <Link to="/privacy" data-testid="footer-privacy" className="hover:text-kawaii-text underline underline-offset-2">Privacy</Link>
        </div>
      </footer>
    </div>
  );
}
